/**
 * Field Selector Component
 * Allows selecting fields including nested relational fields
 * Based on reference: reference/app/src/interfaces/_system/system-fields/
 */

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { X, Plus, GripVertical } from 'lucide-react';
import { FieldList } from './FieldList';
import { useFieldsStore } from '@/stores/fields';
import { cn } from '@/lib/utils';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface FieldSelectorProps {
    collection: string;
    value: string[];
    onChange: (fields: string[]) => void;
    allowSelectAll?: boolean;
    className?: string;
}

// Sortable field item component
interface SortableFieldItemProps {
    fieldKey: string;
    displayName: string;
    onRemove: (fieldKey: string) => void;
}

function SortableFieldItem({ fieldKey, displayName, onRemove }: SortableFieldItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: fieldKey });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                'flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2',
                isDragging && 'opacity-50'
            )}
        >
            <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing touch-none"
            >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            <span className="flex-1 text-sm font-mono">{displayName}</span>
            <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 hover:bg-destructive/10 hover:text-destructive"
                onClick={() => onRemove(fieldKey)}
            >
                <X className="h-4 w-4" />
            </Button>
        </div>
    );
}

export function FieldSelector({
    collection,
    value,
    onChange,
    allowSelectAll = false,
    className,
}: FieldSelectorProps) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const fieldsStore = useFieldsStore();

    // Setup drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Get field display names
    const selectedFieldsWithNames = useMemo(() => {
        return value.map((fieldKey) => {
            const displayName = getFieldDisplayName(fieldKey);
            return {
                key: fieldKey,
                displayName,
            };
        });
    }, [value, collection]);

    function getFieldDisplayName(fieldKey: string): string {
        const fieldParts = fieldKey.split('.');

        const fieldNames = fieldParts.map((part, _index) => {
            // For nested fields, we need to trace through the path
            // For now, just use the part name (TODO: support nested field names)
            const field = fieldsStore.getField(collection, part);
            return field?.name || part;
        });

        return fieldNames.join(' → ');
    }

    const handleAddField = (fieldKey: string) => {
        if (!value.includes(fieldKey)) {
            onChange([...value, fieldKey]);
        }
        setDropdownOpen(false);
    };

    const handleRemoveField = (fieldKey: string) => {
        onChange(value.filter((f) => f !== fieldKey));
    };

    const handleSelectAll = () => {
        // Get all non-alias fields from collection
        const allFields = fieldsStore
            .getFieldsForCollection(collection)
            .filter((f) => f.type !== 'alias')
            .map((f) => f.field);

        onChange(allFields);
        setDropdownOpen(false);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = value.indexOf(active.id as string);
            const newIndex = value.indexOf(over.id as string);

            const newOrder = arrayMove(value, oldIndex, newIndex);
            onChange(newOrder);
        }
    };

    return (
        <div className={cn('space-y-3', className)}>
            {/* Selected Fields with Drag and Drop */}
            {selectedFieldsWithNames.length > 0 && (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={value}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="space-y-2">
                            {selectedFieldsWithNames.map((field) => (
                                <SortableFieldItem
                                    key={field.key}
                                    fieldKey={field.key}
                                    displayName={field.displayName}
                                    onRemove={handleRemoveField}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            {/* Add Field Dropdown */}
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Field
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-80" align="start">
                    {allowSelectAll && (
                        <>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="w-full justify-start mb-2"
                                onClick={handleSelectAll}
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Select All Fields
                            </Button>
                            <div className="border-b mb-2" />
                        </>
                    )}
                    <FieldList
                        collection={collection}
                        disabledFields={value}
                        onAdd={handleAddField}
                        relationalFieldSelectable={true}
                    />
                </DropdownMenuContent>
            </DropdownMenu>

            {selectedFieldsWithNames.length === 0 && (
                <p className="text-xs text-muted-foreground">
                    No fields selected. Leave empty to export all fields.
                </p>
            )}
        </div>
    );
}
